// Tracks the current record's correspondence target for the dock. Explicit page
// registration (useCorrespondenceTarget) wins; otherwise it derives from the
// agent's page context, so the two docks share one notion of "the current record".
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useAgent } from "../agent/AgentProvider";
import { targetFromContext, type CorrespondenceTarget } from "../lib/source-registry";

interface CorrespondenceValue {
  /** Explicit registration wins over the agent-derived target. */
  target: CorrespondenceTarget | null;
  registerTarget(t: CorrespondenceTarget | null): void;
  /** Bumped when something (e.g. a Desk row) asks the dock to open the Correspondence tab. */
  openNonce: number;
  openCorrespondence(): void;
}

const Ctx = createContext<CorrespondenceValue | null>(null);

export function CorrespondenceProvider({ children }: { children: ReactNode }) {
  const { pageContext } = useAgent();
  const [explicit, setExplicit] = useState<CorrespondenceTarget | null>(null);
  const [openNonce, setOpenNonce] = useState(0);
  const derived = useMemo(() => targetFromContext(pageContext), [pageContext]);
  const target = explicit ?? derived;
  const registerTarget = useCallback((t: CorrespondenceTarget | null) => setExplicit(t), []);
  const openCorrespondence = useCallback(() => setOpenNonce((n) => n + 1), []);
  const value = useMemo<CorrespondenceValue>(
    () => ({ target, registerTarget, openNonce, openCorrespondence }),
    [target, registerTarget, openNonce, openCorrespondence],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCorrespondence(): CorrespondenceValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCorrespondence must be used within <CorrespondenceProvider>");
  return v;
}

/** For pages without a URL-derived entity (standup, library doc): register the
 *  record's correspondence target while mounted; clears on unmount. */
export function useCorrespondenceTarget(target: CorrespondenceTarget | null): void {
  const { registerTarget } = useCorrespondence();
  const key = target ? `${target.source}::${target.sourceId}::${target.label ?? ""}` : null;
  useEffect(() => {
    registerTarget(key ? target : null);
    return () => registerTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, registerTarget]);
}
